import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const INITIAL = { code: '', name: '', type: 'asset', isPostable: true };

export function AccountPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/accounting?tab=accounts');
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => post('/accounting/accounts', form),
    onSuccess: () => {
      toast.success(tr('billing', 'account_added'));
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !create.isSuccess && JSON.stringify(form) !== JSON.stringify(INITIAL);

  return (
    <FormScreen
      title={tr('billing', 'add_an_account')}
      description={tr('billing', 'headings_group_only_postable_accounts_take')}
      eyebrow={tr('billing', 'chart_of_accounts')}
      icon={BookOpen}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!form.code || !form.name) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('billing', 'account')} icon={BookOpen}>
        <Field label={tr('billing', 'code')} required>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
        </Field>
        <Field label={tr('billing', 'name')} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={tr('billing', 'type')} required>
          <ChoiceButtons
            layout="chips"
            value={form.type}
            onChange={(type) => setForm({ ...form, type })}
            options={['asset', 'liability', 'equity', 'income', 'expense'].map((t) => ({ value: t, label: humanize(t, 'account_type') }))}
          />
        </Field>
        <Checkbox
          className="self-end"
          checked={form.isPostable}
          onCheckedChange={(checked) => setForm({ ...form, isPostable: checked })}
          label={tr('billing', 'accepts_postings')}
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'add')} loading={create.isPending} incomplete={!form.code || !form.name} cancelTo={returnTo} />
    </FormScreen>
  );
}
